'use client';

import { ExternalLink, Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import type { ElasticBranding } from '@//app/api/search/brandings';
import { Button } from '@//components/Button';
import { Link } from '@//components/Link';
import { Logo } from '@//components/Logo';
import { Modal } from '@//components/Modal';
import { TextContentHtml } from '@//components/TextContent';
import { VStack } from '@//components/VStack';
import { SuggestionBannerContainer } from '@//containers/SuggestionBannerContainer';
import useBrandings from '@//data/search/brandings/useBrandings';
import { useLegacyTrackManufacturerPackagePageGotoSearch } from '@//data/tracking/useLegacyTrackManufacturerPackagePageGotoSearch';
import { markdownToHtml, stripMarkdown } from '@//utils/text';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import { sanitizeParams } from '@//utils/vehicles-search/utils';

interface ManufacturerPackagingSuggestionProps {
  searchParams: URLSearchParams;
  rootUrl: string;
}

export const ManufacturerPackagingSuggestion: React.FC<ManufacturerPackagingSuggestionProps> = ({
  searchParams,
  rootUrl,
}) => {
  const t = useTranslations('pages.search.manufacturerPackagingSuggestion');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  const { trackLegacyManufacturerPackagePageGotoSearch } =
    useLegacyTrackManufacturerPackagePageGotoSearch();

  const brandingsQuery = useBrandings();
  const brandings = brandingsQuery.data;

  // Get selected manufacturers from search params
  const selectedManufacturers = useMemo(
    () => searchParams.getAll('manufacturer'),
    [searchParams.toString()],
  );

  // Find matching manufacturer packaging
  const manufacturerPackaging = useMemo((): ElasticBranding | undefined => {
    // Only show if exactly one manufacturer is selected
    if (selectedManufacturers.length !== 1 || !brandings) {
      return undefined;
    }

    const selectedManufacturer = selectedManufacturers[0];

    // Find branding with "Manufacture packaging" category that matches the selected manufacturer
    // Match using identifier field which corresponds to the manufacturer name
    const packaging = brandings.find(
      (branding) =>
        branding.category.includes('Manufacture packaging') &&
        branding.identifier === selectedManufacturer,
    );

    return packaging;
  }, [selectedManufacturers, brandings]);

  const handleAddFilter = useCallback(() => {
    if (!manufacturerPackaging) return;

    // Track the interaction
    trackLegacyManufacturerPackagePageGotoSearch('top', manufacturerPackaging.name);

    // Add hasManufacturerPackaging filter
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('hasManufacturerPackaging', 'true');
    updateUrl(sanitizeParams(nextParams));

    // Banner will automatically hide because hasManufacturerPackaging=true is now in the URL
    // If user removes the filter later, banner will reappear
  }, [
    manufacturerPackaging,
    searchParams,
    updateUrl,
    trackLegacyManufacturerPackagePageGotoSearch,
  ]);

  const handleReadMore = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const hasManufacturerPackagingFilter = searchParams.get('hasManufacturerPackaging') === 'true';

  // Don't show if no packaging found
  if (!manufacturerPackaging) {
    return null;
  }

  // Don't show if filter is already active (either manually added or via banner)
  // When filter is active and banner was dismissed via button, keep it hidden
  if (hasManufacturerPackagingFilter) {
    return null;
  }

  // If filter is not active, show banner even if it was previously dismissed
  // This allows the banner to reappear when user removes the filter

  // Truncate description for banner (first 200 characters) and strip markdown
  const plainDescription = stripMarkdown(manufacturerPackaging.description);
  const truncatedDescription =
    plainDescription.length > 200 ? `${plainDescription.substring(0, 200)}...` : plainDescription;

  return (
    <>
      <Modal title={manufacturerPackaging.name} isOpen={isModalOpen} onClose={handleCloseModal}>
        <VStack gap={16}>
          <Logo
            src={`${manufacturerPackaging.logo.file.url}?w=120`}
            srcSet={`${manufacturerPackaging.logo.file.url}?w=240 2x`}
            alt={manufacturerPackaging.name}
            width={120}
            height={48}
          />
          <TextContentHtml textSize="md" html={markdownToHtml(manufacturerPackaging.description)} />
          {manufacturerPackaging.website && (
            <Link
              href={manufacturerPackaging.website}
              title={t('readMoreTitle', { name: manufacturerPackaging.name })}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {t('readMore', { name: manufacturerPackaging.name })}
              <ExternalLink aria-hidden="true" />
            </Link>
          )}
        </VStack>
      </Modal>
      <SuggestionBannerContainer
        logo={{
          src: `${manufacturerPackaging.logo.file.url}?w=120`,
          srcSet: `${manufacturerPackaging.logo.file.url}?w=240 2x`,
          alt: manufacturerPackaging.name,
        }}
        description={truncatedDescription}
        action={
          <Button variant="primary" onClick={handleAddFilter}>
            <Plus aria-hidden="true" />
            {t('addFilter')}
          </Button>
        }
        onReadMore={handleReadMore}
        readMoreTitle={t('readMoreTitle', { name: manufacturerPackaging.name })}
      />
    </>
  );
};
